import { getUserFiles } from '@/uppy-file-upload/fetch/get-user-files'
import { isSelectableProcessingStatus, isTerminalProcessingStatus } from '@/user_files/utils/processing-status'

export type FileProcessingWaitResult = 'ready' | 'failed' | 'superseded'

const kPollIntervalMs = 3000

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Poll a freshly-uploaded user file until processing reaches a terminal status — `ready` once it's
 * selectable, `failed` on a terminal failure. Until then, server-derived fields like
 * `detected_content_type` aren't set and the template-selection endpoint rejects the file ("not a
 * DOCX"). Readiness is delegated to the shared processing-status helpers, so this stays in sync with
 * how the rest of the app judges a file selectable/terminal rather than hardcoding status values.
 *
 * Like the app's background file-processing poller, this trusts the backend to reach a terminal
 * status rather than guessing a timeout. `isCurrent` is the caller's supersession check: the moment
 * it returns false (the user picked/uploaded a different template, or cleared it) polling stops with
 * `superseded`, so an out-of-date upload never selects itself — and it's the user's escape hatch if
 * a file never finishes.
 *
 * `sleep` is injectable for deterministic tests.
 */
export async function waitForUserFileProcessed(
  fileId: string,
  isCurrent: () => boolean,
  { sleep = defaultSleep }: { sleep?: (ms: number) => Promise<void> } = {}
): Promise<FileProcessingWaitResult> {
  while (isCurrent()) {
    let status: string | undefined
    try {
      // triggerStatusRefresh nudges the backend to advance files still in a processing state.
      const { files } = await getUserFiles([fileId], true)
      status = files[fileId]?.record.processing_status
    } catch {
      // Transient fetch error — keep polling rather than failing the upload.
    }

    if (!isCurrent()) return 'superseded'
    if (isSelectableProcessingStatus(status)) return 'ready'
    if (isTerminalProcessingStatus(status)) return 'failed' // terminal but not selectable → a failure

    await sleep(kPollIntervalMs)
  }
  return 'superseded'
}
